/// <reference types="vite/client" />
import type {
  Author,
  Project,
  CommercialData,
  BackstageImage,
  PresentationArticle,
} from '../types/api'

const BASE_URL = import.meta.env.DEV
  ? '/api'
  : import.meta.env['VITE_API_URL'] || 'https://api.chapterfour.ru'
const IS_TEST = import.meta.env.MODE === 'test'

const PAGE_ROUTE_PROJECT_FILTERS: Record<string, Record<string, string>> = {
  '/commercial': { category: 'COMMERCIAL' },
  '/3d': { category: '3D' },
  '/broadcast': { category: 'BROADCAST' },
}

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'HttpError'
  }
}

class ApiCache {
  private readonly memory = new Map<string, { data: unknown; expiresAt: number }>()

  constructor(
    private readonly prefix: string,
    private readonly ttl: number,
  ) {}

  private storageKey(path: string): string {
    return `${this.prefix}${path}`
  }

  private readSession(path: string): unknown {
    if (IS_TEST) return null
    try {
      const raw = sessionStorage.getItem(this.storageKey(path))
      if (!raw) return null
      const parsed = JSON.parse(raw) as { data: unknown; expiresAt: number }
      if (!parsed?.expiresAt || Date.now() > parsed.expiresAt) {
        sessionStorage.removeItem(this.storageKey(path))
        return null
      }
      return parsed.data
    } catch {
      return null
    }
  }

  read(path: string): unknown {
    if (IS_TEST) return null
    const inMemory = this.memory.get(path)
    if (inMemory && Date.now() <= inMemory.expiresAt) return inMemory.data

    const fromSession = this.readSession(path)
    if (fromSession !== null) {
      this.memory.set(path, { data: fromSession, expiresAt: Date.now() + this.ttl })
      return fromSession
    }
    return null
  }

  write(path: string, data: unknown): void {
    if (IS_TEST) return
    const payload = { data, expiresAt: Date.now() + this.ttl }
    this.memory.set(path, payload)
    try {
      sessionStorage.setItem(this.storageKey(path), JSON.stringify(payload))
    } catch {
      // ignore storage quota errors
    }
  }
}

const FIVE_MIN = 5 * 60 * 1000
const TEN_MIN = 10 * 60 * 1000

const projectsCache = new ApiCache('projects-cache:', FIVE_MIN)
const authorsCache = new ApiCache('authors-cache:', FIVE_MIN)
const galleriesCache = new ApiCache('galleries-cache:', TEN_MIN)
const commercialCache = new ApiCache('commercial-cache:', FIVE_MIN)

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${BASE_URL}${path}`
  const emptyFallback = (path.includes('/blog/') ? { data: [] } : []) as T

  try {
    const res = await fetch(url, options)
    if (!res.ok) {
      const error = new HttpError(res.status, `HTTP ${res.status}: ${res.statusText}`)
      console.error('API Request failed:', error)
      return emptyFallback
    }
    return res.json() as Promise<T>
  } catch (error) {
    console.error('API Request failed:', error)
    return emptyFallback
  }
}

type CacheOptions = { force?: boolean }

export const api = {
  getAuthors(options: CacheOptions = {}): Promise<Author[]> {
    const path = '/users'
    if (!options.force) {
      const cached = authorsCache.read(path)
      if (cached) return Promise.resolve(cached as Author[])
    }
    return request<Author[]>(path).then((result) => {
      if (Array.isArray(result)) authorsCache.write(path, result)
      return result
    })
  },

  getAuthor(id: string | number, options: CacheOptions = {}): Promise<Author> {
    const path = `/users/${id}`
    if (!options.force) {
      const cached = authorsCache.read(path)
      if (cached) return Promise.resolve(cached as Author)
    }
    return request<Author>(path).then((result) => {
      if (result && !Array.isArray(result)) authorsCache.write(path, result)
      return result
    })
  },

  getAuthorProjects(id: string | number, options: CacheOptions = {}): Promise<Project[]> {
    const path = `/users/${id}/projects`
    if (!options.force) {
      const cached = authorsCache.read(path)
      if (cached) return Promise.resolve(cached as Project[])
    }
    return request<Project[]>(path).then((result) => {
      if (Array.isArray(result)) authorsCache.write(path, result)
      return result
    })
  },

  getProject(id: string | number, options: CacheOptions = {}): Promise<Project> {
    const path = `/projects/${id}`
    if (!options.force) {
      const cached = projectsCache.read(path)
      if (cached) return Promise.resolve(cached as Project)
    }
    return request<Project>(path).then((result) => {
      if (result && !Array.isArray(result)) projectsCache.write(path, result)
      return result
    })
  },

  getAllProjects(
    params: Record<string, string | number | boolean> = {},
    options: CacheOptions = {},
  ): Promise<Project[]> {
    const queryString = new URLSearchParams(params as Record<string, string>).toString()
    const path = queryString ? `/projects?${queryString}` : '/projects'
    if (!options.force) {
      const cached = projectsCache.read(path)
      if (cached) return Promise.resolve(cached as Project[])
    }
    return request<Project[]>(path).then((result) => {
      if (Array.isArray(result)) projectsCache.write(path, result)
      return result
    })
  },

  getProjectsForPageRoute(
    routePath: '/commercial' | '/3d' | '/broadcast',
    options: CacheOptions = {},
  ): Promise<Project[]> {
    const params = PAGE_ROUTE_PROJECT_FILTERS[routePath]
    if (!params) return Promise.resolve([])
    return this.getAllProjects(params, options)
  },

  getKpPageCommercial(options: CacheOptions = {}): Promise<CommercialData> {
    return this.getCommercial(options)
  },

  uploadFile(data: FormData): Promise<{ id: number }> {
    return request('/files/unauthorized', {
      method: 'POST',
      body: data,
    })
  },

  addApplication(data: Record<string, unknown>): Promise<unknown> {
    return request('/applications', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
  },

  getBlogArticle(slug: string): Promise<PresentationArticle> {
    return request(`/blog/${slug}`)
  },

  getCommercial(options: CacheOptions = {}): Promise<CommercialData> {
    const path = '/commercial'
    if (!options.force) {
      const cached = commercialCache.read(path)
      if (cached) return Promise.resolve(cached as CommercialData)
    }
    return request<CommercialData>(path).then((result) => {
      if (result && typeof result === 'object' && !Array.isArray(result)) {
        commercialCache.write(path, result)
      }
      return result
    })
  },

  getCommercialByUser(
    userName: string,
    options: CacheOptions = {},
  ): Promise<{ url?: string; commercial_offer_url?: string; file_url?: string }> {
    const path = `/commercial/${encodeURIComponent(userName)}`
    if (!options.force) {
      const cached = commercialCache.read(path)
      if (cached)
        return Promise.resolve(
          cached as { url?: string; commercial_offer_url?: string; file_url?: string },
        )
    }
    return request<CommercialData>(path).then((result) => {
      if (result && typeof result === 'object' && !Array.isArray(result)) {
        commercialCache.write(path, result)
      }
      return result as { url?: string; commercial_offer_url?: string; file_url?: string }
    })
  },

  getBackstageGallery(options: CacheOptions = {}): Promise<BackstageImage[]> {
    const path = '/backstage-gallery/'
    if (!options.force) {
      const cached = galleriesCache.read(path)
      if (cached) return Promise.resolve(cached as BackstageImage[])
    }
    return request<BackstageImage[]>(path).then((result) => {
      if (Array.isArray(result)) galleriesCache.write(path, result)
      return result
    })
  },
}
